// Shared sanitization helpers used while hydrating a portable snapshot (./selectionFilterSerialization.deserialize.ts)
// against a target data context. Factored out because the same two patterns - "look up a table/columns,
// but tolerate them being absent" and "filter an item-id list down to what exists in the target, with a
// 'dropped N' warning" - repeat across every step type.

import { TpDataContext, TpDataTable, TpDataTableColumn } from '../../interfaces';
import { getDataTable } from '../../../features/visual-analytics/charts/chartDataBindings';
import { getColumnInfo } from '../../analyticsDataSlice';
import { convertCatNameToCatIndex } from '../../../features/visual-analytics/helpers/misc';
import { TpEnSelectionFilterTypes } from '../interfaces';
import type { TpSelectionFilterStep } from './selectionFilterSerialization.schemas';

/** Return the table/column references carried by a portable filter step. */
export function getFilterStepColumnReferences(
  step: TpSelectionFilterStep,
): Array<{ dataTableId: string; columnId: string }> {
  switch (step.type) {
    case TpEnSelectionFilterTypes.CATEGORICAL:
    case TpEnSelectionFilterTypes.INTERSECTIONS:
    case TpEnSelectionFilterTypes.NUMERICALRANGE:
      return [{ dataTableId: step.dataTableId, columnId: step.colId }];
    case TpEnSelectionFilterTypes.DUALCATEGORICAL:
      return [
        { dataTableId: step.dataTableId, columnId: step.colId1 },
        { dataTableId: step.dataTableId, columnId: step.colId2 },
      ];
    case TpEnSelectionFilterTypes.MULTILEVELCAT:
      return step.colIds.map((columnId) => ({ dataTableId: step.dataTableId, columnId }));
    case TpEnSelectionFilterTypes.LASSO2D:
      return [
        { dataTableId: step.dataTableId, columnId: step.colIdX },
        { dataTableId: step.dataTableId, columnId: step.colIdY },
      ];
    case TpEnSelectionFilterTypes.MANUAL:
    case TpEnSelectionFilterTypes.IMPORT:
      return [];
  }
}

/**
 * Looks up a data table and each of its `colIds` in `dataContext`, returning `null` (instead of
 * throwing) if the table or any of the columns is not found. Used by hydration, where a step's
 * referenced table/columns may legitimately be absent from the target data.
 */
export function resolveColumns(
  dataContext: TpDataContext,
  dataTableId: string,
  colIds: string[],
): { dataTable: TpDataTable; columns: TpDataTableColumn[] } | null {
  try {
    const dataTable = getDataTable(dataContext, dataTableId);
    const columns = colIds.map((colId) => getColumnInfo(dataTable, colId));
    return { dataTable, columns };
  } catch {
    return null;
  }
}

/**
 * Filters `ids` down to those present in `targetItemIdSet`, pushing a "dropped N ..." warning onto
 * `warnings` when some are removed. `itemLabel` fills in what was dropped (e.g. "item IDs",
 * "imported item IDs") to keep warning messages specific to the calling step type.
 */
export function sanitizeItemIdsAgainstTarget(
  ids: string[],
  targetItemIdSet: Set<string>,
  warnings: string[],
  itemLabel: string,
): string[] {
  const sanitized = ids.filter((id) => targetItemIdSet.has(id));
  const droppedCount = ids.length - sanitized.length;
  if (droppedCount > 0) {
    warnings.push(`dropped ${droppedCount} ${itemLabel} because they were not found in the target.`);
  }
  return sanitized;
}

/**
 * Converts a category display name to its numeric index for `colInfo`, returning `null` (never
 * throwing) if the category does not exist in the target column.
 */
export function tryConvertCatNameToCatIndex(colInfo: TpDataTableColumn, catName: string): number | null {
  try {
    const catId = convertCatNameToCatIndex(colInfo, catName);
    return Number.isNaN(catId) ? null : catId;
  } catch {
    return null;
  }
}
